
import { supabase } from "@/lib/supabase";
import { logSecurityEvent } from "./securityUtils";

/**
 * Validates a password against our security policy
 * @param password The password to validate
 * @returns Error message or null if valid
 */
export async function validatePasswordSecurely(password: string): Promise<string | null> {
  try {
    // Basic length check
    if (!password || password.length < 8) {
      return "Password must be at least 8 characters long";
    }

    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }

    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }

    // Check for numbers
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }

    // Check for special characters
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character";
    }
    
    // Optional: Check for password breaches via HIBP API
    const isBreached = await checkPasswordBreached(password);
    if (isBreached) {
      return "This password has appeared in a data breach. Please choose a different password.";
    }

    return null; // Password is valid
  } catch (error) {
    console.error("Password validation error:", error);
    return "An error occurred during password validation";
  }
}

/**
 * Computes the strength of a password as a percentage
 * @param password The password to evaluate
 * @returns A number from 0-100 representing password strength
 */
export function getPasswordStrength(password: string): number {
  if (!password) return 0;
  
  let score = 0;
  
  // Length check (up to 40 points)
  if (password.length >= 8) score += 20;
  if (password.length >= 10) score += 10;
  if (password.length >= 12) score += 10;
  
  // Complexity checks (up to 60 points)
  if (/[A-Z]/.test(password)) score += 15;
  if (/[a-z]/.test(password)) score += 15;
  if (/[0-9]/.test(password)) score += 15;
  if (/[^A-Za-z0-9]/.test(password)) score += 15;
  
  return Math.min(score, 100);
}

/**
 * Check if a password has been exposed in known data breaches
 * Uses k-Anonymity model for secure API usage (only the first 5 chars of hash are sent)
 */
export async function checkPasswordBreached(password: string): Promise<boolean> {
  try {
    // Convert password to SHA-1 hash (HIBP API uses SHA-1)
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    
    // Convert hash to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Split the hash - we'll only send the first 5 chars to the API
    const prefix = hashHex.substring(0, 5).toUpperCase();
    const suffix = hashHex.substring(5).toUpperCase();
    
    // Call the HIBP API with k-anonymity
    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: {
        'User-Agent': 'Edaptica-PasswordSecurityCheck'
      }
    });
    
    if (!response.ok) {
      console.error("Password breach check failed:", response.statusText);
      return false; // Fail open - assume not breached if API fails
    }
    
    // Check if our hash suffix is in the response
    const text = await response.text();
    const breachData = text.split('\n');
    
    for (const line of breachData) {
      const [hashSuffix] = line.split(':');
      if (hashSuffix === suffix) {
        return true; // Password has been breached
      }
    }
    
    return false; // Password not found in breaches
  } catch (error) {
    console.error("Error checking password breach:", error);
    return false; // Fail open - assume not breached if check fails
  }
}

/**
 * Saves password hash history for the user
 * This is used to prevent password reuse
 */
export async function savePasswordHistory(userId: string, passwordHash: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('password_history')
      .insert({ user_id: userId, password_hash: passwordHash });
    
    if (error) {
      throw error;
    }
    
    await logSecurityEvent(
      userId,
      'password_update',
      'Password updated and added to history',
      { timestamp: new Date().toISOString() }
    );
  } catch (error) {
    console.error("Failed to save password history:", error);
  }
}

/**
 * Check if the password has been used before by this user
 */
export async function isPasswordPreviouslyUsed(userId: string, passwordHash: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('password_history')
      .select('id')
      .eq('user_id', userId)
      .eq('password_hash', passwordHash)
      .limit(1);
    
    if (error) {
      throw error;
    }
    
    return data && data.length > 0;
  } catch (error) {
    console.error("Failed to check password history:", error);
    return false; // Fail open - assume not reused if check fails
  }
}
